/**
 * Time-series line chart, hand-rolled SVG (Recharts is ~95 KB gz; Lift said no
 * for the same reason). Series are drawn as a 2px line, or as recessive dots
 * for raw readings behind a trend. One y-axis, always. A crosshair + tooltip
 * follows the pointer; with 2+ series there is a legend, and the last point of
 * each line is direct-labelled so identity never rests on colour alone.
 */

import { useMemo, useRef, useState } from 'react'

import { daysBetween, shortDate } from '../../lib/days'

export type Series = {
  id: string
  label: string
  color: string
  kind?: 'line' | 'dots'
  dashed?: boolean
  points: { day: string; y: number }[]
}

const W = 320
const PAD = { l: 38, r: 44, t: 10, b: 22 }

export function LineChart({ series, height = 180, format, empty }: {
  series: Series[]
  height?: number
  format: (y: number) => string
  empty?: string
}) {
  const ref = useRef<SVGSVGElement>(null)
  const [hover, setHover] = useState<string | null>(null)

  const g = useMemo(() => {
    const all = series.flatMap((s) => s.points)
    // Two readings on one day is not a trend: need two distinct days.
    if (new Set(all.map((p) => p.day)).size < 2) return null
    const days = all.map((p) => p.day).sort()
    const d0 = days[0], d1 = days[days.length - 1]
    const span = Math.max(1, daysBetween(d0, d1))
    let lo = Math.min(...all.map((p) => p.y)), hi = Math.max(...all.map((p) => p.y))
    const pad = Math.max((hi - lo) * 0.1, Math.abs(hi) * 0.005, 0.5)
    lo -= pad; hi += pad
    const H = height
    const x = (day: string) => PAD.l + (daysBetween(d0, day) / span) * (W - PAD.l - PAD.r)
    const y = (v: number) => PAD.t + (1 - (v - lo) / (hi - lo)) * (H - PAD.t - PAD.b)
    const ticks = [lo + (hi - lo) * 0.15, (lo + hi) / 2, hi - (hi - lo) * 0.15]
    return { d0, d1, span, x, y, ticks, H }
  }, [series, height])

  if (!g) return <p className="muted">{empty ?? 'Not enough data yet.'}</p>

  const onMove = (e: React.PointerEvent) => {
    const svg = ref.current
    if (!svg) return
    const r = svg.getBoundingClientRect()
    const px = ((e.clientX - r.left) / r.width) * W
    let best: string | null = null, bd = Infinity
    for (const s of series) for (const p of s.points) {
      const d = Math.abs(g.x(p.day) - px)
      if (d < bd) { bd = d; best = p.day }
    }
    setHover(best)
  }

  const hoverX = hover ? g.x(hover) : null
  const at = hover ? series.map((s) => ({ s, p: s.points.find((p) => p.day === hover) }))
    .filter((v) => v.p) : []

  return (
    <div style={{ position: 'relative' }}>
      {series.length > 1 && (
        <div className="row small" style={{ justifyContent: 'flex-start', flexWrap: 'wrap', gap: '.9rem', marginBottom: '.3rem' }}>
          {series.map((s) => (
            <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '.35rem', color: 'var(--muted)' }}>
              <svg width="14" height="8" aria-hidden>{s.kind === 'dots'
                ? <circle cx="7" cy="4" r="3" fill={s.color} />
                : <line x1="0" y1="4" x2="14" y2="4" stroke={s.color} strokeWidth="2" strokeDasharray={s.dashed ? '3 2' : undefined} />}
              </svg>
              {s.label}
            </span>
          ))}
        </div>
      )}
      <svg ref={ref} viewBox={`0 0 ${W} ${g.H}`} width="100%" role="img"
        aria-label={series.map((s) => s.label).join(', ')}
        onPointerMove={onMove} onPointerDown={onMove} onPointerLeave={() => setHover(null)}
        style={{ touchAction: 'pan-y', display: 'block' }}>
        {g.ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.l} x2={W - PAD.r} y1={g.y(t)} y2={g.y(t)} stroke="var(--border)" strokeWidth="1" />
            <text x={PAD.l - 5} y={g.y(t) + 3.5} textAnchor="end" fontSize="9.5" fill="var(--muted)">{format(t)}</text>
          </g>
        ))}
        <text x={PAD.l} y={g.H - 5} fontSize="9.5" fill="var(--muted)">{shortDate(g.d0)}</text>
        <text x={W - PAD.r} y={g.H - 5} fontSize="9.5" fill="var(--muted)" textAnchor="end">{shortDate(g.d1)}</text>

        {series.map((s) => s.kind === 'dots'
          ? s.points.map((p) => <circle key={s.id + p.day} cx={g.x(p.day)} cy={g.y(p.y)} r="2.4" fill={s.color} />)
          : s.points.length > 0 && (
            <polyline key={s.id} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round"
              strokeLinecap="round" strokeDasharray={s.dashed ? '4 3' : undefined}
              points={s.points.map((p) => `${g.x(p.day)},${g.y(p.y)}`).join(' ')} />
          ))}

        {series.filter((s) => s.kind !== 'dots' && s.points.length).map((s) => {
          const p = s.points[s.points.length - 1]
          return (
            <g key={s.id + 'end'}>
              <circle cx={g.x(p.day)} cy={g.y(p.y)} r="4" fill={s.color} stroke="var(--surface)" strokeWidth="2" />
              <text x={g.x(p.day) + 7} y={g.y(p.y) + 3.5} fontSize="10" fill="var(--text)" fontWeight="600">{format(p.y)}</text>
            </g>
          )
        })}

        {hoverX !== null && (
          <line x1={hoverX} x2={hoverX} y1={PAD.t} y2={g.H - PAD.b} stroke="var(--muted)" strokeWidth="1" strokeDasharray="2 2" />
        )}
      </svg>
      {hover && at.length > 0 && (
        <div className="chart-tip" style={{ left: `${Math.min(70, Math.max(5, (hoverX! / W) * 100 - 10))}%`, top: 0 }}>
          <div style={{ fontWeight: 600 }}>{shortDate(hover)}</div>
          {at.map(({ s, p }) => (
            <div key={s.id}><span style={{ color: s.color }}>●</span> {s.label}: {format(p!.y)}</div>
          ))}
        </div>
      )}
    </div>
  )
}
